import React from 'react';
import { isEqual } from 'lodash';
import { Ad, CSP } from '../rql';
import CSPSquareCard from './csp_square_card';
import CSPWideCard from './csp_wide_card';
import { ConditionFilter } from '../cms/lib/metadata_helper';
import gql from 'graphql-tag';
import OverflowingRow from './overflowing_row';
import Tiles from './tiles';
import { isExperimentEnabled } from '../user_context_helpers';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';

import { withUserContext, IUser, IUserContext } from './user_context_provider';
import { trackEvent, trackCSPView } from '../elog/mparticle_tracker';
import { AdCard } from './ads/ad_card';

export interface IProps {
  title?: JSX.Element | React.ReactNode | string;
  subtitleHtml?: string;
  ledeHtml?: string;
  csps: CSP[];
  conditionFilter?: ConditionFilter;
  loading: boolean;
  action?: React.ReactElement;
  expectedCount?: number;
  displayAsGrid?: boolean;
  hideMetadata?: boolean;
  id?: string;
  user?: IUser;
  shouldLinkToSellForm?: boolean;
  trackingComponentName?: string;
  shouldTrack?: boolean;
  trackingQuery?: string;
  ad?: Ad;
  renderAsCondensed?: boolean;
}

export const CSPAdditionalTrackingFieldsFragment = gql`
  fragment CSPAdditionalTrackingFields on CSP {
    _id
    id
    slug
    brand {
      _id
      slug
    }
    categories {
      _id
      slug
      rootSlug
      parentCategories {
        _id
        slug
      }
    }
    inventory {
      usedTotal
      newTotal
    }
  }
`;

export const CSPCardFragment = gql`
  fragment CSPCard on CSP {
    _id
    id
    title
    slug
    averageReviewRating
    reviewsCount
    webLink {
      path
      href
    }
    newListingsLink {
      path
    }
    usedListingsLink {
      path
    }
    image(input: {transform: "card_square"}) {
      source
    }
    brand {
      _id
      slug
      name
    }
    categories {
      _id
      slug
      rootSlug
      name
      parentCategories {
        _id
        name
      }
    }
    inventory {
      newLowPrice {
        display
        amount
        amountCents
        currency
      }
      usedLowPrice {
        display
        amount
        amountCents
        currency
      }
      usedTotal
      newTotal
    }
    finishes
    ... CSPAdditionalTrackingFields
  }
  ${CSPAdditionalTrackingFieldsFragment}
  `;

export class CSPRow extends React.Component<IProps & IUserContext, null> {
  componentName() {
    return this.props.trackingComponentName || 'CSPRow';
  }

  displayStyle() {
    return this.getCount() > 1 ? 'compareText' : null;
  }

  experiments() {
    return this.props.user?.experiments && formatExperimentsForEventAttributes(this.props.user, [
      experiments.CSP_CARD,
      experiments.CONDENSED_CSP_CARD,
    ]);
  }

  componentDidMount() {
    const csps = this.props?.csps as CSP[];
    if (!csps.length || !this.props.shouldTrack) { return; }

    trackEvent(trackCSPView(csps, this.componentName(), this.displayStyle(), this.experiments()));
  }

  componentDidUpdate(oldProps) {
    const newCSPs = this.props?.csps as CSP[];
    const oldCSPs = oldProps?.csps as CSP[];

    if (!newCSPs.length || !this.props.shouldTrack) { return; }

    if (!isEqual(newCSPs, oldCSPs)) {
      trackEvent(trackCSPView(newCSPs, this.componentName(), this.displayStyle(), this.experiments()));
    }
  }

  cspCardExperimentActive() {
    return this.props.user?.experiments && isExperimentEnabled(this.props.user, experiments.CSP_CARD);
  }

  getCount() {
    if (this.props.loading) return this.props.expectedCount;

    return this.props.csps.length;
  }

  shouldBeOneWide() {
    if (this.props.renderAsCondensed) return false;
    return this.getCount() === 1 && !this.props.ad;
  }

  shouldGrow() {
    if (this.props.renderAsCondensed) return false;
    if (!this.props.displayAsGrid) return true;
    if (this.props.displayAsGrid && this.getCount() <= 3) return true;
  }

  shouldRenderAsHorizontal(): boolean {
    if (this.props.renderAsCondensed) return false;
    return this.getCount() < 4;
  }

  renderPlaceholder() {
    if (this.props.expectedCount === 1) {
      return <CSPWideCard />;
    }

    return <CSPSquareCard />;
  }

  renderCards() {
    const { conditionFilter, csps } = this.props;

    if (this.shouldBeOneWide()) {
      return (
        <CSPWideCard
          conditionFilter={conditionFilter}
          csp={csps[0]}
          hideMetadata={this.props.hideMetadata}
          shouldLinkToSellForm={this.props.shouldLinkToSellForm}
          trackingQuery={this.props.trackingQuery}
        />
      );
    }

    return csps.map((page, idx) => (
      <CSPSquareCard
        conditionFilter={conditionFilter}
        csp={page}
        key={page.id}
        position={idx}
        hideMetadata={this.props.hideMetadata}
        renderAsHorizontal={this.shouldRenderAsHorizontal()}
        renderAsCondensed={this.props.renderAsCondensed}
        shouldLinkToSellForm={this.props.shouldLinkToSellForm}
        compareCSPCardActive={this.cspCardExperimentActive()}
        trackingQuery={this.props.trackingQuery}
      />
    ));
  }

  render() {
    if (!this.props.loading && !this.props.csps.length) return null;

    return (
      <OverflowingRow
        title={this.props.title}
        subtitleHtml={this.props.subtitleHtml}
        ledeHtml={this.props.ledeHtml}
        action={this.props.action}
        collectionCount={this.getCount()}
        id={this.props.id}
      >
        <Tiles
          placeholder={this.renderPlaceholder()}
          loading={this.props.loading}
          expectedCount={this.props.expectedCount}
          singleRow={!this.props.displayAsGrid}
          oneWide={this.shouldBeOneWide()}
          largeTiles={!this.props.renderAsCondensed && !this.shouldBeOneWide()}
          grow={this.shouldGrow()}
        >
          {
            this.props.ad &&
            <AdCard
              ad={this.props.ad}
              key={this.props.ad.uuid}
              componentName={this.componentName()}
            />
          }
          {this.renderCards()}
        </Tiles>
      </OverflowingRow>
    );
  }
}

export default withUserContext(CSPRow);
